"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

const AUTO_HIDE_DELAY = 5000;
const TRACK_HEIGHT = 188;
const THUMB_HEIGHT = 48;

type ThumbState = {
  height: number;
  isScrollable: boolean;
  top: number;
};

type DragState = {
  pointerStartY: number;
  previousBodyUserSelect: string;
  scrollStartY: number;
  target: HTMLElement | null;
};

function isDocumentScrollLocked() {
  if (typeof document === "undefined") {
    return false;
  }

  return (
    document.documentElement.classList.contains("app-popup-scroll-locked") ||
    document.body.classList.contains("app-popup-scroll-locked")
  );
}

function getCustomScrollContainer() {
  if (typeof document === "undefined") {
    return null;
  }

  return (
    Array.from(
      document.querySelectorAll<HTMLElement>("[data-custom-scroll-container]"),
    ).find((element) => {
      const overflowY = window.getComputedStyle(element).overflowY;

      return (
        element.getClientRects().length > 0 &&
        (overflowY === "auto" ||
          overflowY === "scroll" ||
          overflowY === "overlay")
      );
    }) ?? null
  );
}

function getScrollMetrics(
  requestedTarget?: HTMLElement | null,
): ThumbState {
  if (typeof window === "undefined" || isDocumentScrollLocked()) {
    return { height: 0, isScrollable: false, top: 0 };
  }

  const target =
    requestedTarget === undefined
      ? getCustomScrollContainer()
      : requestedTarget;
  const documentElement = document.documentElement;
  const viewportHeight = target?.clientHeight ?? window.innerHeight;
  const scrollHeight =
    target?.scrollHeight ??
    Math.max(documentElement.scrollHeight, document.body.scrollHeight);
  const scrollTop = target?.scrollTop ?? window.scrollY;
  const maxScroll = Math.max(0, scrollHeight - viewportHeight);

  if (maxScroll <= 0) {
    return { height: 0, isScrollable: false, top: 0 };
  }

  const height = THUMB_HEIGHT;
  const maxThumbTop = Math.max(0, TRACK_HEIGHT - height);
  const progress = Math.min(1, Math.max(0, scrollTop / maxScroll));
  const top = progress * maxThumbTop;

  return { height, isScrollable: true, top };
}

export function CustomScrollbar() {
  const pathname = usePathname();
  const hideTimerRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const previousPathnameRef = useRef(pathname);
  const [isVisible, setIsVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [thumbState, setThumbState] = useState<ThumbState>(() => ({ height: 0, isScrollable: false, top: 0 }));

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      if (!dragStateRef.current) {
        setIsVisible(false);
      }
    }, AUTO_HIDE_DELAY);
  }, [clearHideTimer]);

  const updateThumb = useCallback(() => {
    if (animationFrameRef.current) {
      return;
    }

    animationFrameRef.current = window.requestAnimationFrame(() => {
      animationFrameRef.current = null;
      const nextState = getScrollMetrics();
      setThumbState(nextState);

      if (nextState.isScrollable) {
        setIsVisible(true);
        scheduleHide();
      } else {
        setIsVisible(false);
      }
    });
  }, [scheduleHide]);

  useEffect(() => {
    const customScrollContainer = getCustomScrollContainer();

    if (
      previousPathnameRef.current !== pathname &&
      customScrollContainer
    ) {
      customScrollContainer.scrollTo({ top: 0 });
    }

    previousPathnameRef.current = pathname;
    updateThumb();

    const revealOnPointerNearEdge = (event: PointerEvent) => {
      if (isDocumentScrollLocked()) {
        setIsVisible(false);
        return;
      }

      if (window.innerWidth - event.clientX <= 48) {
        updateThumb();
      }
    };

    const handlePopupScrollLock = (event: Event) => {
      const locked = (event as CustomEvent<{ locked?: boolean }>).detail?.locked ?? isDocumentScrollLocked();

      if (locked) {
        const previousBodyUserSelect =
          dragStateRef.current?.previousBodyUserSelect ?? "";

        dragStateRef.current = null;
        document.body.style.userSelect = previousBodyUserSelect;
        setIsDragging(false);
        setIsVisible(false);
      }

      updateThumb();
    };

    const resizeObserver = new ResizeObserver(updateThumb);
    const observeCustomScrollContent = () => {
      if (!customScrollContainer) {
        return;
      }

      resizeObserver.disconnect();
      resizeObserver.observe(customScrollContainer);
      Array.from(customScrollContainer.children).forEach((child) => {
        resizeObserver.observe(child);
      });
    };
    const mutationObserver = customScrollContainer
      ? new MutationObserver(() => {
          observeCustomScrollContent();
          updateThumb();
        })
      : null;

    if (customScrollContainer) {
      observeCustomScrollContent();
      mutationObserver?.observe(customScrollContainer, {
        childList: true,
        subtree: true,
      });
    } else {
      resizeObserver.observe(document.documentElement);
    }

    window.addEventListener("scroll", updateThumb, { passive: true });
    document.addEventListener("scroll", updateThumb, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", updateThumb);
    window.addEventListener("wheel", updateThumb, { passive: true });
    window.addEventListener("touchmove", updateThumb, { passive: true });
    window.addEventListener("keydown", updateThumb);
    window.addEventListener("pointermove", revealOnPointerNearEdge, { passive: true });
    window.addEventListener("app-popup-scroll-lock", handlePopupScrollLock);

    return () => {
      window.removeEventListener("scroll", updateThumb);
      document.removeEventListener("scroll", updateThumb, true);
      window.removeEventListener("resize", updateThumb);
      window.removeEventListener("wheel", updateThumb);
      window.removeEventListener("touchmove", updateThumb);
      window.removeEventListener("keydown", updateThumb);
      window.removeEventListener("pointermove", revealOnPointerNearEdge);
      window.removeEventListener("app-popup-scroll-lock", handlePopupScrollLock);
      mutationObserver?.disconnect();
      resizeObserver.disconnect();
      clearHideTimer();

      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [clearHideTimer, pathname, updateThumb]);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;

      if (!dragState || isDocumentScrollLocked()) {
        return;
      }

      const documentElement = document.documentElement;
      const target =
        dragState.target?.isConnected === false ? null : dragState.target;
      const scrollHeight =
        target?.scrollHeight ??
        Math.max(documentElement.scrollHeight, document.body.scrollHeight);
      const viewportHeight = target?.clientHeight ?? window.innerHeight;
      const maxScroll = Math.max(0, scrollHeight - viewportHeight);
      const maxThumbTop = Math.max(1, TRACK_HEIGHT - thumbState.height);
      const pointerDelta = event.clientY - dragState.pointerStartY;
      const scrollDelta = (pointerDelta / maxThumbTop) * maxScroll;

      if (target) {
        target.scrollTo({ top: dragState.scrollStartY + scrollDelta });
      } else {
        window.scrollTo({ top: dragState.scrollStartY + scrollDelta });
      }

      setThumbState(getScrollMetrics(target));
    };

    const endDragging = () => {
      const dragState = dragStateRef.current;

      if (!dragState) {
        return;
      }

      dragStateRef.current = null;
      setIsDragging(false);
      document.body.style.userSelect = dragState.previousBodyUserSelect;
      scheduleHide();
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", endDragging, { once: true });
    window.addEventListener("pointercancel", endDragging, { once: true });
    window.addEventListener("blur", endDragging, { once: true });

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", endDragging);
      window.removeEventListener("pointercancel", endDragging);
      window.removeEventListener("blur", endDragging);

      const activeDragState = dragStateRef.current;

      if (activeDragState) {
        dragStateRef.current = null;
        document.body.style.userSelect =
          activeDragState.previousBodyUserSelect;
      }
    };
  }, [isDragging, scheduleHide, thumbState.height]);

  if (!thumbState.isScrollable) {
    return null;
  }

  return (
    <div aria-hidden="true" className={`custom-scrollbar-layer${isVisible || isDragging ? " is-visible" : ""}`}>
      <div
        className="custom-scrollbar-thumb"
        onPointerDown={(event) => {
          if (isDocumentScrollLocked()) {
            setIsVisible(false);
            return;
          }

          event.preventDefault();
          clearHideTimer();
          const target = getCustomScrollContainer();
          dragStateRef.current = {
            pointerStartY: event.clientY,
            previousBodyUserSelect: document.body.style.userSelect,
            scrollStartY: target?.scrollTop ?? window.scrollY,
            target,
          };
          document.body.style.userSelect = "none";
          setIsDragging(true);
          setIsVisible(true);
        }}
        style={{
          height: thumbState.height,
          transform: `translate3d(0, ${thumbState.top}px, 0)`,
        }}
      />
    </div>
  );
}
